// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';

import {
  addVitalSign,
  deleteVitalSign,
  updateVitalSign,
} from '../../../actions/progressNoteActions';

import Table from '../../Table.jsx';
import Modal from '../../Modal.jsx';
import { Button, PrimaryButton, DeleteButton } from '../../Buttons.jsx';
import { Error } from '../../Typography.jsx';

import ViewVitalSigns from './ViewVitalSigns.jsx';
import EditVitalSigns from './EditVitalSigns.jsx';

import getThresholds from './getThresholds';


type Props = {
  dob: string,
  vitalSigns: Object[],
  wellChildChecks: Object[],
  activePatientId: string,
  dob: string,
  activeAccount: Object,
  addVitalSign: Function,
  deleteVitalSign: Function,
  updateVitalSign: Function,
  print: boolean,
  // if problem object passed in, vitals table filters to entries between onset/resolution dates
  problem?: Object,
}

type State = {
  modalOpen: bool,
  viewOnly: bool,
  editRow: ?Object,
}

class VitalSignsTableComponent extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      modalOpen: false,
      viewOnly: false,
      editRow: null,
    };
  }

  // Modal state handlers. editRow is set to row object if editing, else null.
  handleEditRow = (row: Object) => {
    this.setState({
      modalOpen: true,
      viewOnly: false,
      editRow: row,
    });
  }

  handleViewRow = (row: Object) => {
    this.setState({
      modalOpen: true,
      viewOnly: true,
      editRow: row,
    });
  }

  handleAddRow = () => {
    this.setState({
      modalOpen: true,
      viewOnly: false,
      editRow: null,
    });
  }

  closeModal = () => {
    this.setState({
      modalOpen: false,
      editRow: null,
    });
  }

  // Misc functions
  /* eslint-disable no-param-reassign */
  transformData(formValues: Object) {
    formValues.date = moment(formValues.date, 'DD-MM-YYYY, HH:mm').toISOString();
    if (formValues.temperatureUnit === 'c') {
      formValues.temperature = (formValues.temperature * 9 / 5 + 32).toFixed(1);
    }
    // add active account for edit history
    const { activeAccount } = this.props;
    formValues.editor = `${activeAccount.name}, ${activeAccount.credential}`;
    formValues.editDate = moment().toISOString();
  }

  getAge(item: Object) {
    const dob = moment(this.props.dob);
    const date = moment(item.date);
    const years = date.diff(dob, 'years');
    dob.add(years, 'years');
    const months = date.diff(dob, 'months');
    return `${years} y, ${months} m`;
  }

  // Endpoint action handlers
  handleDeleteRow = (id: string) => {
    this.props.deleteVitalSign(this.props.activePatientId, id);
  }

  handleSaveRow = (id: string, formData: Object) => {
    this.transformData(formData.values);
    this.props.updateVitalSign(this.props.activePatientId, {
      progressNoteId: id,
      ...formData.values,
    });
  }

  handleSaveNewRow = (formData: Object) => {
    this.transformData(formData.values);
    this.props.addVitalSign(this.props.activePatientId, formData.values);
  }

  // Create action column for each row
  actionsAccessor = (row: Object) => {
    if (row.fromWCC) {
      return (
        <div>
          <Button onClick={() => this.handleViewRow(row)}>View</Button>
          {' (from WCC)'}
        </div>
      );
    }
    const defaultButtons = (
      <div>
        <Button onClick={() => this.handleViewRow(row)}>View</Button>
        <PrimaryButton onClick={() => this.handleEditRow(row)}>Edit</PrimaryButton>
        <DeleteButton onClick={() => this.handleDeleteRow(row._id)}>X</DeleteButton>
      </div>
    );
    return defaultButtons;
  }

  render() {
    let vitalSigns = this.props.vitalSigns.slice();
    for (let i = 0; i < this.props.wellChildChecks.length; i++) {
      const {
        date, notes, vitals, editors, editDates,
      } = this.props.wellChildChecks[i];
      const {
        rr, hr, temperature, bp, spo2,
      } = vitals;
      if (rr || hr || temperature || bp || spo2) {
        vitalSigns.push({
          ...vitals,
          date,
          notes,
          editors,
          editDates,
          fromWCC: true,
        });
      }
    }
    vitalSigns.sort((a, b) => {
      if (!a.date) {
        return 1;
      }
      if (!b.date) {
        return -1;
      }
      if (a.date < b.date) {
        return 1;
      }
      if (b.date < a.date) {
        return -1;
      }
      return 0;
    });

    vitalSigns = vitalSigns.map((item) => {
      const thresholds = getThresholds(this.props.dob, item.date);
      return {
        ...item,
        ...thresholds,
      };
    });

    const { problem } = this.props;
    if (problem && problem.onsetDate) {
      vitalSigns = vitalSigns.filter(item => moment(item.date) >= moment(problem.onsetDate).startOf('day'));
    }
    if (problem && problem.resolutionDate) {
      vitalSigns = vitalSigns.filter(item => moment(item.date) <= moment(problem.resolutionDate).endOf('day'));
    }

    const SPO2_MIN_THRESHOLD = 95;
    const columns = [
      {
        Header: 'Date',
        id: 'date',
        cell: item => (item.date ? moment(item.date).format() : '--'),
        width: 100,
      },
      {
        Header: 'Time',
        id: 'time',
        cell: item => (item.date && !item.fromWCC ? moment(item.date).format('HH:mm') : '--'),
        width: 100,
      },
      {
        Header: 'Temperature',
        id: 'temperature',
        cell: (item) => {
          const { temperature } = item;
          if (temperature === undefined || temperature === null) {
            return '--';
          }
          const tempC = ((temperature - 32) * 5 / 9).toFixed(1);
          const tempString = `${temperature} °F (${tempC} °C)`;
          if (temperature > item.tempMax) {
            return <Error>{`${tempString} (H)`}</Error>;
          }
          if (temperature < item.tempMin) {
            return <Error>{`${tempString} (L)`}</Error>;
          }
          return tempString;
        },
      },
      {
        Header: 'RR (breaths per min)',
        id: 'rr',
        cell: (item) => {
          if (item.rr === undefined || item.rr === null) {
            return '--';
          }
          if (item.rr > item.rrMax) {
            return <Error>{`${item.rr} (H)`}</Error>;
          }
          if (item.rr < item.rrMin) {
            return <Error>{`${item.rr} (L)`}</Error>;
          }
          return item.rr;
        },
      },
      {
        Header: 'HR (bpm)',
        id: 'hr',
        cell: (item) => {
          if (item.hr === undefined || item.hr === null) {
            return '--';
          }
          if (item.hr > item.hrMax) {
            return <Error>{`${item.hr} (H)`}</Error>;
          }
          if (item.hr < item.hrMin) {
            return <Error>{`${item.hr} (L)`}</Error>;
          }
          return item.hr;
        },
      },
      {
        Header: 'BP',
        id: 'bp',
        cell: (item) => {
          if (item.bp === undefined || item.bp === null || item.bp === '') {
            return '--';
          }
          let [bpS, bpD] = item.bp.split('/');
          if (bpS > item.bpSMax) {
            bpS = <Error>{`${bpS} (H)`}</Error>;
          } else if (bpS < item.bpSMin) {
            bpS = <Error>{`${bpS} (L)`}</Error>;
          }
          if (bpD > item.bpDMax) {
            bpD = <Error>{`${bpD} (H)`}</Error>;
          } else if (bpD < item.bpDMin) {
            bpD = <Error>{`${bpD} (L)`}</Error>;
          }
          return <span>{bpS}/{bpD}</span>;
        },
      },
      {
        Header: 'SpO2',
        id: 'spo2',
        cell: (item) => {
          if (item.spo2 === undefined || item.spo2 === null) {
            return '--';
          }
          if (item.spo2 < SPO2_MIN_THRESHOLD) {
            return <Error>{`${item.spo2}% (L)`}</Error>;
          }
          return `${item.spo2}%`;
        },
      },
    ];

    return (
      <div>
        {!this.props.print && <PrimaryButton onClick={this.handleAddRow}>Add Row</PrimaryButton>}
        <Table
          columns={columns}
          data={vitalSigns}
          extraActions={this.actionsAccessor}
          actionsWidth={140}
          style={{ maxHeight: `${problem ? '400px' : ''}` }}
        />
        <Modal isOpen={this.state.modalOpen}>
          {this.state.viewOnly
            ? <ViewVitalSigns
              viewRow={this.state.editRow}
              closeModal={this.closeModal}
            />
            : <EditVitalSigns
              editRow={this.state.editRow}
              handleSaveRow={this.handleSaveRow}
              handleSaveNewRow={this.handleSaveNewRow}
              closeModal={this.closeModal}
            />
          }
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  activePatientId: state.patient.data.activePatientId,
  vitalSigns: state.patient.data.progressNotes.vitalSigns,
  wellChildChecks: state.patient.data.wellChildChecks,
  dob: state.patient.data.generalInfo.dob,
  activeAccount: state.account.activeAccount,
  print: state.patient.print,
});

const mapDispatchToProps = dispatch => ({
  addVitalSign:
    (patientId, vitalSign) => dispatch(addVitalSign(patientId, vitalSign)),
  deleteVitalSign:
    (patientId, vitalSignId) => dispatch(deleteVitalSign(patientId, vitalSignId)),
  updateVitalSign:
    (patientId, vitalSign) => dispatch(updateVitalSign(patientId, vitalSign)),
});

const VitalSignsTable = connect(
  mapStateToProps,
  mapDispatchToProps,
)(VitalSignsTableComponent);

export default VitalSignsTable;



// WEBPACK FOOTER //
// ./client/src/components/patientPages/vitals/VitalSignsTable.jsx